import { useEffect, useState } from 'react';
import type { Table } from '@tanstack/react-table';
import type { DataTableRow } from '@/shared/ui/data-table/types';

/** What the toolbar renders from: the search box value and the column picker. */
export type UseTableToolbarState = {
  searchText: string;
  onSearchInput: (value: string) => void;
  toggleColumn: (id: string, visible: boolean) => void;
  hideableColumnIds: string[];
};

export type UseTableToolbarOptions = {
  /** Current search text, or undefined when the table has no search. */
  searchValue: string | undefined;
  onSearchChange: ((value: string) => void) | undefined;
  debounceMs: number;
};

/**
 * Search and column-visibility state for the table toolbar.
 *
 * The search term is debounced before it reaches the server: a query per
 * keystroke is a request storm, and on a large collection it is also slow
 * enough to feel broken. The input itself stays fully controlled and
 * immediate — only the request is deferred.
 */
export function useTableToolbar<TData extends DataTableRow>(
  table: Table<TData>,
  options: UseTableToolbarOptions,
): UseTableToolbarState {
  const { searchValue, onSearchChange, debounceMs } = options;
  const externalSearch = searchValue ?? '';
  const [searchText, setSearchText] = useState(externalSearch);
  const [seenExternalSearch, setSeenExternalSearch] = useState(externalSearch);

  /**
   * Mirroring a prop during render, not in an effect.
   *
   * A caller that owns the search term is the source of truth, so when it
   * changes the box must show the new term in the SAME render. An effect would
   * paint the previous term for one frame and then correct it — and each sync
   * is a second render of every row in the table to draw a string that was
   * already known. This is the documented adjust-state-during-render pattern,
   * and it is conditional on the prop actually differing, so React discards the
   * extra render and keeps the one it is already producing.
   */
  if (externalSearch !== seenExternalSearch) {
    setSeenExternalSearch(externalSearch);
    setSearchText(externalSearch);
  }

  useEffect(() => {
    if (!onSearchChange) return;
    const timer = setTimeout(() => {
      if (searchText !== externalSearch) onSearchChange(searchText);
    }, debounceMs);
    return () => clearTimeout(timer);
  }, [searchText, externalSearch, onSearchChange, debounceMs]);

  return {
    searchText,
    onSearchInput: setSearchText,
    toggleColumn: (id, visible) => table.getColumn(id)?.toggleVisibility(visible),
    hideableColumnIds: table
      .getAllLeafColumns()
      .filter((column) => column.getCanHide())
      .map((column) => column.id),
  };
}